import { Chart } from '../Enums/Chart.enum';
import { getOptionsForChartByName } from '../Utils/chartOptions';
import { getDefaultOptionsForChartByName } from '../Utils/chartsDefaultOptions';
import {
	ChartDatasetColor,
	chartDatasetColors,
} from '../Utils/chartDatasetColors';
import useTranslation from '../../../Hooks/useTranslation';
import { Languages } from '../../../Enums/Common';
import { Moment } from 'moment-timezone';
import { getMomentDate } from '../../../Utils/dates.util';
import { useAppContext } from '../../../Services/AppContext';

export interface ChartData {
	labels: string[];
	datasets: ChartDataset[];
}

interface ChartDataset {
	label: string;
	key: string;
	data: (number | string)[];
	unit: string;
	fill?: boolean;
	cubicInterpolationMode?: string;
	backgroundColor?: string;
	borderColor?: string;
	pointBackgroundColor?: string;
	borderWidth?: number;
	pointRadius?: number;
	sources?: (string | null)[];
	questionnaires?: (string | null)[];
}

export default function useCharts() {
	const { t } = useTranslation();
	const { dateFormatMoment } = useAppContext();

	const getChartsData = (
		data: any,
		chartName: Chart,
		view: string,
		language: Languages,
		allowZero?: boolean,
	) => {
		const chartLabels = getLabels(data);
		const { datasets, hasVal } = createDatasets(chartLabels, data, allowZero);
		const chartData: ChartData = {
			labels: chartLabels,
			datasets,
		};
		const chartOptions = getOptionsByName(chartName, hasVal, view, language);
		return { chartData, chartOptions };
	};

	const getLabels = (data: any = []) => {
		const xAxes: string[] = [];
		for (const dataset of data) {
			dataset.data.map((item: { time: Moment; value: string }) => {
				const label = getMomentDate(item.time).format('x');

				if (!xAxes.includes(label)) {
					xAxes.push(label);
				}
			});
		}
		return xAxes;
	};

	const createDatasets = (
		forLabels: string[],
		fromData: any,
		allowZero?: boolean,
	) => {
		const datasets: ChartDataset[] = [];
		let hasVal = false;
		for (const item of fromData) {
			const { datasetData, hasValues } = getDataForDataset(
				forLabels,
				item.data,
				allowZero,
			);
			const sources = getSourcesForDataset(forLabels, item.data);
			const questionnaires = getQuestionnairesForDataset(forLabels, item.data);
			const dataset: ChartDataset = {
				label: item.description,
				key: item.key,
				unit: item.unit ?? '',
				data: datasetData,
				sources: sources,
				questionnaires: questionnaires,
				fill: true,
				cubicInterpolationMode: 'monotone',
				borderWidth: 2,
				pointRadius: 3,
				...getColorForDatasetByKey(item.key),
			};
			if (!hasVal) {
				hasVal = hasValues;
			}
			datasets.push(dataset);
		}
		return { datasets, hasVal };
	};

	const getDataForDataset = (
		forLabels: string[],
		data: any,
		allowZero?: boolean,
	) => {
		const datasetData: (number | string)[] = [];
		let hasValues = false;
		forLabels.map(label => {
			let value: string | number = 'NaN';
			const item = data.find(
				(d: { time: Moment; value: string }) =>
					getMomentDate(d.time).format('x') === label,
			);
			if (item && (allowZero || item.value != 0)) {
				value = parseFloat(item.value);
				hasValues = true;
			}
			datasetData.push(value);
		});
		return { datasetData, hasValues };
	};

	const getSourcesForDataset = (forLabels: string[], data: any) => {
		const sources: (string | null)[] = [];
		forLabels.map(label => {
			let value: string | null = null;
			const item = data.find(
				(d: { time: Moment; source: string }) =>
					getMomentDate(d.time).format('x') === label,
			);
			if (item && item.source?.title) {
				value = item.source.title;
			}
			sources.push(value);
		});
		return sources;
	};

	const getQuestionnairesForDataset = (forLabels: string[], data: any) => {
		const sources: (string | null)[] = [];
		forLabels.map(label => {
			let value: string | null = null;
			const item = data.find(
				(d: { time: Moment; questionnaire: any }) =>
					getMomentDate(d.time).format('x') === label,
			);
			if (item && item.questionnaire?.title) {
				value = item.questionnaire.title;
			}
			sources.push(value);
		});
		return sources;
	};

	const getColorForDatasetByKey = (key: string) => {
		const color: ChartDatasetColor | undefined = chartDatasetColors.find(
			c => c.key === key,
		);
		if (color) {
			return {
				pointBackgroundColor: color.pointBackgroundColor,
				backgroundColor: color.backgroundColor,
				borderColor: color.borderColor,
			};
		}
	};

	const getOptionsByName = (
		chartName: Chart,
		hasValues: boolean,
		view: string,
		language: Languages,
	) => {
		const options = !hasValues
			? getDefaultOptionsForChartByName(
					chartName,
					view,
					t(dateFormatMoment) ?? '',
					language,
			  )
			: getOptionsForChartByName(
					chartName,
					view,
					t,
					language,
					dateFormatMoment,
			  );
		return options ? options : null;
	};

	return { getChartsData };
}
